import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import JplModal from './JplModal';
import { can } from '../utils/permissions';
import { formatDate, displayMonth, displayRange } from '../utils/dates';
import { money as fmtMoney, moneyWhole } from '../utils/format';
import { IconButton, Banner, ICONS } from './ui';
import { useAuth } from '../contexts/AuthContext';
import { timesheetPath, jobPath } from '../nav/paths';

// The panels a user can put on their Dashboard (picked in Settings > My Settings).
// `available(user)` hides panels whose data the user cannot see.
export const DASHBOARD_PANELS = [
  { key: 'billablePct', label: 'Billable % (you and your staff)', available: (u) => !!u?.resourceNo },
  { key: 'invoices', label: 'Invoices to create / fix', available: (u) => !!u?.resourceNo && can(u, 'createInvoices') },
  { key: 'expenses', label: 'Expenses to code', available: () => true },
  { key: 'pmJobs', label: 'Jobs where you are PM', available: (u) => !!u?.resourceNo && can(u, 'createInvoices') },
  { key: 'budgetNegative', label: 'Open tasks with no budget left', available: (u) => !!u?.resourceNo && can(u, 'budget') },
  { key: 'billableJobs', label: 'Billable jobs by timesheet', available: () => true },
  { key: 'rateExceptions', label: 'Rate sheet exceptions (unbilled T&M)', available: (u) => !!u?.resourceNo },
];

// A panel's size on the grid: w / h = 1 or 2 (double width / double height),
// saved per user with the panel order (user.dashboardLayout).
export const panelSize = (user, key) => {
  const l = (user && user.dashboardLayout && user.dashboardLayout[key]) || {};
  return { w: l.w === 2 ? 2 : 1, h: l.h === 2 ? 2 : 1 };
};
// The size of the panel being rendered, so a double-height panel can show more.
const PanelSizeContext = createContext({ w: 1, h: 1 });
// Height for a panel's scrolling list: the normal height, plus a grid row when
// the panel is double height.
const useListHeight = (base) => (useContext(PanelSizeContext).h === 2 ? base + 232 : base);

// The user's chosen panels that they can see, in the user's chosen order.
export function dashboardPanelsFor(user) {
  const byKey = new Map(DASHBOARD_PANELS.map((p) => [p.key, p]));
  return ((user && user.dashboardPanels) || [])
    .map((k) => byKey.get(k))
    .filter((p) => p && p.available(user));
}

// Advanced-settings picker: the chosen panels in dashboard order (drag a row, or
// use the arrows, to move it; uncheck to remove), then the other available
// panels (check to add at the end). Built from DASHBOARD_PANELS, so new panels
// appear here automatically. onChange(nextKeys) saves the new order.
export function DashboardPanelPicker({ user, saving, onChange }) {
  const chosen = dashboardPanelsFor(user).map((p) => p.key);
  const others = DASHBOARD_PANELS.filter((p) => p.available(user) && !chosen.includes(p.key));
  const label = (k) => (DASHBOARD_PANELS.find((p) => p.key === k) || {}).label || k;
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const move = (from, to) => {
    if (from === to || to < 0 || to >= chosen.length) return;
    const next = [...chosen];
    const [k] = next.splice(from, 1);
    next.splice(to, 0, k);
    onChange(next);
  };
  const row = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 6px', borderRadius: 6 };
  const arrow = { background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '0 6px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' };
  return (
    <div>
      {chosen.map((k, i) => (
        <div key={k}
          draggable={!saving}
          onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', k); }}
          onDragOver={(e) => { e.preventDefault(); if (dragIdx != null) setOverIdx(i); }}
          onDragLeave={() => setOverIdx(null)}
          onDrop={(e) => { e.preventDefault(); if (dragIdx != null) move(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
          onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
          style={{
            ...row,
            cursor: saving ? 'default' : 'grab',
            background: overIdx === i && dragIdx !== i ? 'var(--primary-light, #dbeafe)' : 'var(--gray-50, #f9fafb)',
            opacity: dragIdx === i ? 0.5 : 1,
            marginBottom: 3,
          }}>
          <span style={{ color: 'var(--gray-400)', userSelect: 'none' }}>&#x2807;</span>
          <span style={{ width: 18, textAlign: 'right', color: 'var(--gray-500)' }}>{i + 1}</span>
          <input type="checkbox" checked disabled={saving} style={{ width: 16, height: 16 }}
            onChange={() => onChange(chosen.filter((x) => x !== k))} />
          <span style={{ flex: 1 }}>{label(k)}</span>
          <button type="button" style={arrow} disabled={saving || i === 0} onClick={() => move(i, i - 1)} aria-label="Move up">&#9650;</button>
          <button type="button" style={arrow} disabled={saving || i === chosen.length - 1} onClick={() => move(i, i + 1)} aria-label="Move down">&#9660;</button>
        </div>
      ))}
      {others.map((p) => (
        <label key={p.key} style={{ ...row, cursor: saving ? 'default' : 'pointer', paddingLeft: 38 }}>
          <input type="checkbox" checked={false} disabled={saving} style={{ width: 16, height: 16 }}
            onChange={() => onChange([...chosen, p.key])} />
          <span>{p.label}</span>
        </label>
      ))}
    </div>
  );
}

const money = (v) => fmtMoney(v || 0);
const linkBtn = { background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', padding: 0, font: 'inherit', textAlign: 'left' };
const bigNum = { fontSize: 32, fontWeight: 700, lineHeight: 1.1, color: 'var(--gray-900, #111827)' };
const muted = { fontSize: 12, color: 'var(--gray-500)' };

function parseInvoiceDocs(s) {
  return (s || '').split(';').map((x) => x.trim()).filter(Boolean).map((x) => {
    const i = x.lastIndexOf(':');
    return { docNo: i >= 0 ? x.slice(0, i) : x, isPosted: i >= 0 && x.slice(i + 1) === '1' };
  });
}

// One dashboard box: title (links to the full screen) + body.
function Panel({ title, onOpen, children }) {
  return (
    <div className="card" style={{ margin: 0, padding: 12, display: 'flex', flexDirection: 'column', minHeight: 150, height: '100%' }}>
      <button type="button" style={{ ...linkBtn, fontSize: 14, fontWeight: 600, marginBottom: 8 }} onClick={onOpen}>
        {title} &rsaquo;
      </button>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function Status({ state }) {
  if (state.loading) return <div style={muted}>Loading…</div>;
  if (state.error) return <div style={{ ...muted, color: '#b91c1c' }}>{state.error}</div>;
  return null;
}

// Load one data set when needed: { loading, error, data }.
// `refresh` is a counter; bumping it reloads the data set.
function useData(needed, load, refresh) {
  const [state, setState] = useState({ loading: needed, error: '', data: null });
  useEffect(() => {
    if (!needed) return undefined;
    let alive = true;
    setState({ loading: true, error: '', data: null });
    load()
      .then((data) => { if (alive) setState({ loading: false, error: '', data }); })
      .catch((e) => { if (alive) setState({ loading: false, error: (e && e.message) || 'Failed to load', data: null }); });
    return () => { alive = false; };
  }, [needed, load, refresh]);
  return state;
}

const loadBillable = () => api.getDashboardBillableByMonth();
const loadCreateJobs = () => api.getCreateInvoiceJobs();
const loadBudgets = () => api.getJobBudgets();
const loadExpenses = () => api.getDashboardExpenses();
const loadBillableJobs = () => api.getDashboardBillableJobs();
const loadRateExceptions = () => api.getDashboardRateExceptions();

// ---- 1. Billable % for the user and their staff: line graph, last 3 months ----
const LINE_COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#db2777', '#65a30d', '#7c3aed', '#ea580c'];
function BillablePctPanel({ state, me, onOpen }) {
  const months = (state.data && state.data.months) || [];
  const people = (state.data && state.data.people) || [];
  // SVG coordinates: y = 0..max% (at least 100), x = the 3 months.
  const W = 300; const H = 150; const L = 30; const R = 10; const T = 8; const B = 20;
  const maxPct = Math.max(100, ...people.flatMap((p) => p.pct.map((v) => v || 0)));
  const x = (i) => L + (months.length > 1 ? (i * (W - L - R)) / (months.length - 1) : (W - L - R) / 2);
  const y = (v) => T + (H - T - B) * (1 - (v || 0) / maxPct);
  const color = (i) => LINE_COLORS[i % LINE_COLORS.length];
  const chartMax = useListHeight(200);
  return (
    <Panel title="Billable %" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (people.length === 0 ? <div style={muted}>No billable data.</div> : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxHeight: chartMax, display: 'block' }} role="img" aria-label="Billable % by month">
            {[0, 50, 100].filter((g) => g <= maxPct).map((g) => (
              <g key={g}>
                <line x1={L} x2={W - R} y1={y(g)} y2={y(g)} stroke="#e5e7eb" strokeWidth="1" />
                <text x={L - 4} y={y(g) + 3} fontSize="9" textAnchor="end" fill="#6b7280">{g}%</text>
              </g>
            ))}
            {months.map((m, i) => (
              <text key={m} x={x(i)} y={H - 6} fontSize="9" textAnchor="middle" fill="#6b7280">
                {formatDate(m, { month: 'short', year: '2-digit' })}
              </text>
            ))}
            {/* Staff first, the user's own line last so it draws on top. */}
            {people.map((p, i) => ({ p, i })).sort((a, b) => (a.p.resourceNo === me) - (b.p.resourceNo === me)).map(({ p, i }) => {
              const mine = p.resourceNo === me;
              const pts = p.pct.map((v, k) => `${x(k)},${y(v)}`).join(' ');
              return (
                <g key={p.resourceNo}>
                  <polyline points={pts} fill="none" stroke={color(i)} strokeWidth={mine ? 3 : 1.5} />
                  {p.pct.map((v, k) => (
                    <circle key={k} cx={x(k)} cy={y(v)} r={mine ? 3.5 : 2.5} fill={color(i)}>
                      <title>{`${p.name} — ${displayMonth(months[k])}: ${(v || 0).toFixed(0)}%`}</title>
                    </circle>
                  ))}
                </g>
              );
            })}
          </svg>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11, marginTop: 4, maxHeight: 60, overflowY: 'auto' }}>
            {people.map((p, i) => (
              <span key={p.resourceNo} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: p.resourceNo === me ? 600 : 400 }}>
                <span style={{ width: 10, height: 3, background: color(i), display: 'inline-block' }} />
                {p.name} {(p.pct[p.pct.length - 1] || 0).toFixed(0)}%
              </span>
            ))}
          </div>
        </>
      ))}
    </Panel>
  );
}

// ---- 2. Invoices to create, and rejected invoices to fix (PM jobs) ----
function InvoicesPanel({ state, onOpen, onOpenJob }) {
  const jobs = (state.data && state.data.jobs) || [];
  const toCreate = jobs.filter((j) => j.ApprovalJobFlag === 'Need Invoice'
    || (j.HasNeededTaskLine && !['Posted Invoice', 'Unposted Invoice'].includes(j.InvoiceStatus)));
  const rejected = [];
  jobs.forEach((j) => parseInvoiceDocs(j.InvoiceDocs).forEach((d) => {
    const dec = !d.isPosted && (j.ApprovalDecisions || {})[d.docNo];
    if (dec && dec.decision === 'Rejected') rejected.push({ job: j, docNo: d.docNo, reason: dec.reason });
  }));
  const listMax = useListHeight(110);
  const list = (items, render) => (
    <div style={{ maxHeight: listMax, overflowY: 'auto', fontSize: 12 }}>{items.map(render)}</div>
  );
  return (
    <Panel title="Invoices" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (jobs.length === 0 ? <div style={muted}>No active jobs as project manager.</div> : (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 6 }}>
            <div><div style={bigNum}>{toCreate.length}</div><div style={muted}>to create</div></div>
            <div><div style={{ ...bigNum, color: rejected.length ? '#9a3412' : bigNum.color }}>{rejected.length}</div><div style={muted}>rejected</div></div>
          </div>
          {rejected.length > 0 && list(rejected, (r) => (
            <div key={r.docNo} style={{ margin: '2px 0' }}>
              <button type="button" style={linkBtn} onClick={() => onOpenJob(r.job.JobNo)}>{r.job.JobNo}</button>
              {' '}#{r.docNo}{r.reason ? <span style={{ color: 'var(--gray-500)' }}> — {r.reason}</span> : null}
            </div>
          ))}
          {toCreate.length > 0 && list(toCreate, (j) => (
            <div key={j.JobNo} style={{ margin: '2px 0' }}>
              <button type="button" style={linkBtn} onClick={() => onOpenJob(j.JobNo)}>{j.JobNo}</button>
              {' '}<span style={{ color: 'var(--gray-600)' }}>{j.JobDescription}</span>
            </div>
          ))}
        </>
      ))}
    </Panel>
  );
}

// ---- 3. Expense entries still to be coded ----
function ExpensesPanel({ state, onOpen }) {
  const d = state.data || {};
  return (
    <Panel title="Expenses" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (
        <>
          <div style={bigNum}>{d.toCode || 0}</div>
          <div style={muted}>to be coded{d.unsubmitted ? ` (of ${d.unsubmitted} not yet submitted)` : ''}</div>
        </>
      )}
    </Panel>
  );
}

// ---- 4. Number of jobs where the user is PM ----
function PmJobsPanel({ state, onOpen }) {
  const jobs = (state.data && state.data.jobs) || [];
  return (
    <Panel title="My PM jobs" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (
        <>
          <div style={bigNum}>{jobs.length}</div>
          <div style={muted}>active job{jobs.length === 1 ? '' : 's'}</div>
        </>
      )}
    </Panel>
  );
}

// ---- 5. Open job tasks with zero or negative budget remaining ----
function BudgetPanel({ state, onOpen, onOpenJob }) {
  const listMax = useListHeight(130);
  const tasks = [];
  ((state.data && state.data.jobs) || []).forEach((j) => (j.tasks || []).forEach((t) => {
    // Expenses task lines (Job Task Type = 3) are excluded, as in
    // GetJobTasksOverBudgetForEmail.
    if (Number(t.taskTypeOption) !== 3 && (Number(t.budgetRemaining) || 0) <= 0.005) tasks.push({ job: j, task: t });
  }));
  tasks.sort((a, b) => a.task.budgetRemaining - b.task.budgetRemaining);
  return (
    <Panel title="No budget left" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (
        <>
          <div style={bigNum}>{tasks.length}</div>
          <div style={muted}>open task{tasks.length === 1 ? '' : 's'}</div>
          <div style={{ maxHeight: listMax, overflowY: 'auto', fontSize: 12, marginTop: 6 }}>
            {tasks.map(({ job, task }) => (
              <div key={`${job.jobNo}|${task.jobTaskNo}`} style={{ display: 'flex', gap: 6, margin: '2px 0' }}>
                <button type="button" style={linkBtn} onClick={() => onOpenJob(job.jobNo)}>{job.jobNo}</button>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={task.description}>
                  {task.jobTaskNo} {task.description}
                </span>
                <span style={{ color: task.budgetRemaining < 0 ? '#b91c1c' : 'var(--gray-600)' }}>{money(task.budgetRemaining)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

// ---- 6. Billable jobs charged on each recent timesheet ----
function BillableJobsPanel({ state, onOpen, onOpenTimesheet }) {
  const chartH = useListHeight(120);
  const sheets = (state.data && state.data.timesheets) || [];
  const max = Math.max(1, ...sheets.map((s) => s.billableJobs));
  return (
    <Panel title="Billable jobs by timesheet" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (sheets.length === 0 ? <div style={muted}>No timesheets yet.</div> : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: chartH, paddingTop: 4 }}>
          {sheets.map((s) => (
            <button key={s.timesheetId} type="button" onClick={() => onOpenTimesheet(s.timesheetId)}
              title={`${displayRange(s.periodStart, s.periodEnd)}: ${s.billableJobs} billable job(s)`}
              style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{s.billableJobs}</span>
              <div style={{ width: '70%', height: `${(s.billableJobs / max) * 75}%`, minHeight: 2, background: '#60a5fa', borderRadius: '3px 3px 0 0' }} />
              <span style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2, whiteSpace: 'nowrap' }}>
                {formatDate(s.periodStart, { month: 'numeric', day: 'numeric' })}
              </span>
            </button>
          ))}
        </div>
      ))}
    </Panel>
  );
}

// ---- 7. Rate sheet exceptions on unbilled T&M time (user's own time, and jobs
// where the user is PM or Director) — same test as the Create Invoices warning ----
// Whole-dollar rates without cents ($150), cents only when present ($150.50).
const rate = (v) => (v == null ? '—' : moneyWhole(v));
function RateExceptionsPanel({ state, onOpen, onOpenLines }) {
  const listMax = useListHeight(180);
  const rows = (state.data && state.data.rows) || [];
  const cell = { padding: '2px 4px', fontSize: 12, whiteSpace: 'nowrap' };
  const head = { ...cell, textAlign: 'left', color: 'var(--gray-500)', fontWeight: 600, position: 'sticky', top: 0, background: 'var(--card-bg, #fff)' };
  return (
    <Panel title="Rate sheet exceptions" onOpen={onOpen}>
      <Status state={state} />
      {!state.loading && !state.error && (rows.length === 0 ? <div style={muted}>None.</div> : (
        <div style={{ maxHeight: listMax, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={head}>Employee</th><th style={head}>Job</th>
              <th style={{ ...head, textAlign: 'right' }}>Normal</th><th style={{ ...head, textAlign: 'right' }}>Charged</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.jobNo}|${r.text}`} title={r.text} style={{ borderTop: '1px solid var(--gray-100)' }}>
                  <td style={cell}>
                    <button type="button" style={linkBtn} onClick={() => onOpenLines(r)}>{r.resourceNo}</button>
                  </td>
                  <td style={{ ...cell, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${r.jobNo} ${r.jobDescription}`}>{r.jobNo}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{rate(r.normalRate)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: '#9a3412' }}>{rate(r.chargedRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Panel>
  );
}

export default function DashboardView({ user }) {
  const navigate = useNavigate();
  const panels = dashboardPanelsFor(user);
  const has = (k) => panels.some((p) => p.key === k);

  const [refresh, setRefresh] = useState(0); // bump to reload every panel
  const billable = useData(has('billablePct'), loadBillable, refresh);
  const createJobs = useData(has('invoices') || has('pmJobs'), loadCreateJobs, refresh);
  const budgets = useData(has('budgetNegative'), loadBudgets, refresh);
  const expenses = useData(has('expenses'), loadExpenses, refresh);
  const billableJobs = useData(has('billableJobs'), loadBillableJobs, refresh);
  const rateExceptions = useData(has('rateExceptions'), loadRateExceptions, refresh);
  const anyLoading = [billable, createJobs, budgets, expenses, billableJobs, rateExceptions].some((d) => d.loading);
  const [jpl, setJpl] = useState(null); // { job, resource, resourceName } planning lines open

  const nav = (path) => navigate(path);
  const openJob = (job, tab) => navigate(jobPath(job, tab));

  // Arrange mode: drag panels to new places and make them double width/height.
  // While arranging, { order, layout } is a working copy; ✔ saves it.
  const { refreshUser } = useAuth();
  const [arranging, setArranging] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dragging, setDragging] = useState(null);
  const dragKey = useRef(null);
  const startArrange = () => {
    setSaveError('');
    setArranging({
      order: panels.map((p) => p.key),
      layout: Object.fromEntries(panels.map((p) => [p.key, panelSize(user, p.key)])),
    });
  };
  const saveArrange = async () => {
    setSaving(true);
    setSaveError('');
    try {
      // Chosen panels not shown right now (no access at the moment) keep their place at the end.
      const hidden = ((user && user.dashboardPanels) || []).filter((k) => !arranging.order.includes(k));
      await api.setPreferences({
        dashboardPanels: [...arranging.order, ...hidden],
        dashboardLayout: { ...((user && user.dashboardLayout) || {}), ...arranging.layout },
      });
      await refreshUser();
      setArranging(null);
    } catch (e) {
      setSaveError((e && e.message) || 'Failed to save the dashboard layout');
    } finally {
      setSaving(false);
    }
  };
  const toggleSize = (key, dim) => setArranging((a) => ({
    ...a, layout: { ...a.layout, [key]: { ...a.layout[key], [dim]: a.layout[key][dim] === 2 ? 1 : 2 } },
  }));
  // Drag by the handle (mouse or touch): the held panel takes the place of the
  // panel under the pointer.
  const dragStart = (key) => (e) => {
    e.preventDefault();
    dragKey.current = key;
    setDragging(key);
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const dragMove = (e) => {
    const key = dragKey.current;
    if (!key) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const target = el && el.closest ? el.closest('[data-panel]') : null;
    const over = target && target.getAttribute('data-panel');
    if (!over || over === key) return;
    setArranging((a) => {
      const order = [...a.order];
      const from = order.indexOf(key);
      const to = order.indexOf(over);
      if (from < 0 || to < 0) return a;
      order.splice(from, 1);
      order.splice(to, 0, key);
      return { ...a, order };
    });
  };
  const dragEnd = () => { dragKey.current = null; setDragging(null); };

  const byKey = new Map(panels.map((p) => [p.key, p]));
  const shown = arranging ? arranging.order.map((k) => byKey.get(k)).filter(Boolean) : panels;
  const sizeOf = (key) => (arranging ? arranging.layout[key] : panelSize(user, key));

  const renderPanel = (p) => {
    switch (p.key) {
      case 'billablePct':
        return <BillablePctPanel key={p.key} state={billable} me={user && user.resourceNo} onOpen={() => navigate('/analytics/billable')} />;
      case 'invoices':
        return <InvoicesPanel key={p.key} state={createJobs} onOpen={() => nav('/jobs/invoicing')} onOpenJob={(j) => openJob(j, 'invoicing')} />;
      case 'expenses':
        return <ExpensesPanel key={p.key} state={expenses} onOpen={() => navigate('/expenses')} />;
      case 'pmJobs':
        return <PmJobsPanel key={p.key} state={createJobs} onOpen={() => nav('/jobs/budget')} />;
      case 'budgetNegative':
        return <BudgetPanel key={p.key} state={budgets} onOpen={() => nav('/jobs/budget')} onOpenJob={(j) => openJob(j, 'budget')} />;
      case 'billableJobs':
        return <BillableJobsPanel key={p.key} state={billableJobs} onOpen={() => navigate('/time/timesheets')} onOpenTimesheet={(id) => navigate(timesheetPath(id))} />;
      case 'rateExceptions':
        return <RateExceptionsPanel key={p.key} state={rateExceptions}
          onOpen={can(user, 'createInvoices') ? () => nav('/jobs/invoicing') : () => {}}
          onOpenLines={(r) => setJpl({ job: r.jobNo, resource: r.resourceNo, resourceName: r.resourceName })} />;
      default:
        return null;
    }
  };

  return (
    <div className="wide-section dash-wide">
    <div className="dash-toolbar">
      {arranging ? (
        <>
          <IconButton icon="save" title="Save this layout" onClick={saveArrange} disabled={saving} />
          <IconButton icon="cancel" title="Cancel arranging" onClick={() => setArranging(null)} disabled={saving} />
        </>
      ) : (
        <>
          <IconButton icon="edit" title="Arrange panels (move and resize)" onClick={startArrange} />
          <IconButton icon="refresh" title="Refresh the dashboard" onClick={() => setRefresh((n) => n + 1)} disabled={anyLoading} />
        </>
      )}
    </div>
    <Banner kind="err" onClose={() => setSaveError('')}>{saveError}</Banner>
    <div className={`dash-grid${arranging ? ' dash-arranging' : ''}`}>
      {shown.map((p) => {
        const sz = sizeOf(p.key);
        return (
          <div key={p.key} data-panel={p.key}
            className={`dash-cell${sz.w === 2 ? ' dash-w2' : ''}${sz.h === 2 ? ' dash-h2' : ''}${dragging === p.key ? ' dash-dragging' : ''}`}>
            {arranging && (
              <div className="dash-arrange-bar">
                <span className="dash-handle" title="Drag to move" aria-label="Drag to move" role="button"
                  onPointerDown={dragStart(p.key)} onPointerMove={dragMove} onPointerUp={dragEnd} onPointerCancel={dragEnd}>
                  {ICONS.drag}
                </span>
                <IconButton icon="wider" className="dash-only-wide" active={sz.w === 2}
                  title={sz.w === 2 ? 'Normal width' : 'Double width'} onClick={() => toggleSize(p.key, 'w')} />
                <IconButton icon="taller" active={sz.h === 2}
                  title={sz.h === 2 ? 'Normal height' : 'Double height'} onClick={() => toggleSize(p.key, 'h')} />
              </div>
            )}
            <PanelSizeContext.Provider value={sz}>{renderPanel(p)}</PanelSizeContext.Provider>
          </div>
        );
      })}
    </div>
    {jpl && <JplModal job={jpl.job} task="" resource={jpl.resource} resourceName={jpl.resourceName} onClose={() => setJpl(null)} />}
    </div>
  );
}
